import React, { useEffect, useState } from 'react'
import { Field, Form, Formik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { boardingPoint, boardingRemove } from '@assets/icons'
import Svg from '@components/svg'
import Button from '@components/button'
import Column from '@components/column'
import Row from '@components/row'
import Fields from '@components/fields'
import { Location } from '@apis/index'
import {
  AddedPointsWrapper,
  LeftColumn,
  RightColumn,
  StyledFormRow,
  StylePointCard,
  StylePointText,
} from './style'
import useIsMobile from '@hooks/useIsMobile'

const LocationSetupForm = () => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [updatedPoint, setUpdatedPoint] = useState<any>({})
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  const { state } = useLocation()
  const [initialValues, setInitialValues] = useState<any>({
    locationName: '',
    basicPrice: '',
    boardingPoint: '',
    boardingPointsList: [] as { name: string }[],
    image: '',
  })

  useEffect(() => {
    if (state?.id) {
      // Check if we have saved order in localStorage
      const savedOrderKey = `locationPointsOrder_${state.id}`
      const savedOrder = localStorage.getItem(savedOrderKey)

      let points = state.points
      if (savedOrder) {
        try {
          const order = JSON.parse(savedOrder)
          // Reorder points based on saved order
          points = order
            .map(
              (id: string) =>
                state.points.find((p: any) => p.id === id) ||
                state.points.find((p: any) => p.name === id),
            )
            .filter(Boolean)
        } catch (e) {
          console.error('Error parsing saved order:', e)
        }
      }

      setInitialValues({
        locationName: state.name.toLowerCase(),
        basicPrice: state.minPrice,
        boardingPointsList: points,
        image: state.image,
      })
    }
  }, [state])

  const handleSubmit = async (values: typeof initialValues, { resetForm }) => {
    const payload = {
      name: values.locationName,
      minPrice: Number(values.basicPrice),
      points: values.boardingPointsList.map((p: any) => {
        if (typeof p === 'string') {
          return { name: p }
        }
        return { id: p.id, name: p.name }
      }),
      image:
        typeof values.image === 'string'
          ? values.image
          : { extension: values.image.extension, base64: values.image.base64 },
    }

    if (state?.id) {
      console.log(payload)
      await Location.update({ state: 'location', id: state.id, data: payload })

      // Save the order to localStorage after update
      if (state.id) {
        const orderKey = `locationPointsOrder_${state.id}`
        const order = values.boardingPointsList.map((p: any) => p.id || p.name)
        localStorage.setItem(orderKey, JSON.stringify(order))
      }

      navigate('/admin/location-setup', { state: null })
      window.location.reload()
    } else {
      await Location.create({ state: 'location', data: payload })
      navigate('/admin/location-setup', { state: null })
    }
    resetForm({
      locationName: '',
      basicPrice: '',
      boardingPoint: '',
      boardingPointsList: [],
      image: '',
    })
    setInitialValues({
      locationName: '',
      basicPrice: '',
      boardingPoint: '',
      boardingPointsList: [],
      image: '',
    })
  }

  // Handle drag start event
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.dataTransfer.setData('text/plain', index.toString())
    setDraggedIndex(index)
    e.currentTarget.style.opacity = '0.5'
  }

  // Handle drag over event
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  // Handle drop event
  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    form: any,
    dropIndex: number,
  ) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))

    if (dragIndex !== dropIndex) {
      const items = [...form.values.boardingPointsList]
      const [draggedItem] = items.splice(dragIndex, 1)
      items.splice(dropIndex, 0, draggedItem)

      form.setFieldValue('boardingPointsList', items)

      // Save the new order to localStorage if we're editing an existing location
      if (state?.id) {
        const orderKey = `locationPointsOrder_${state.id}`
        const order = items.map((p: any) => p.id || p.name)
        localStorage.setItem(orderKey, JSON.stringify(order))
      }
    }

    setDraggedIndex(null)
  }

  // Handle drag end event
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1'
    setDraggedIndex(null)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue }) => (
        <Form>
          <StyledFormRow>
            <LeftColumn>
              <Column>
                <Row>
                  <div style={{ width: '85%' }}>
                    <Fields.Input
                      name="locationName"
                      label="Location Name"
                      placeholder="Select departure city"
                    />
                  </div>
                  <div style={{ width: '85%' }}>
                    <Fields.Input
                      name="basicPrice"
                      label="Basic Price"
                      placeholder="Enter Price"
                    />
                  </div>
                </Row>

                <Fields.Input
                  hasAddButon
                  name="boardingPoint"
                  label="Add Boarding/Dropoff Point"
                  placeholder="Enter Point"
                  styleInput="border-top-right-radius:0; border-bottom-right-radius:0"
                />

                <p>Added Points</p>
                <Field name="boardingPointsList">
                  {({ form }) =>
                    form.values.boardingPointsList.length > 0 && (
                      <AddedPointsWrapper>
                        {form.values.boardingPointsList.map(
                          (point: any, index: number) => {
                            const pointObj =
                              typeof point === 'string'
                                ? { name: point }
                                : point

                            return (
                              <StylePointCard
                                key={index}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, form, index)}
                                onDragEnd={handleDragEnd}
                                style={{
                                  cursor: 'grab',
                                  opacity: draggedIndex === index ? 0.5 : 1,
                                  backgroundColor:
                                    draggedIndex === index
                                      ? '#f0f0f0'
                                      : 'white',
                                }}
                              >
                                <Svg src={boardingPoint} />

                                {state?.id ? (
                                  // Editable mode (all inputs active)
                                  <input
                                    value={pointObj.name || ''}
                                    size={pointObj.name?.length || 1}
                                    onChange={(e) => {
                                      const updatedList = [
                                        ...form.values.boardingPointsList,
                                      ]
                                      updatedList[index] = {
                                        ...pointObj,
                                        name: e.target.value,
                                      }
                                      form.setFieldValue(
                                        'boardingPointsList',
                                        updatedList,
                                      )
                                    }}
                                    style={{
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '4px 8px',
                                      outline: 'none',
                                      fontSize: '14px',
                                    }}
                                  />
                                ) : (
                                  // Normal view (no editing)
                                  <StylePointText>
                                    {pointObj.name}
                                  </StylePointText>
                                )}

                                {/* Show ID only if available */}
                                {!state?.id && (
                                  <StylePointText>{pointObj.id}</StylePointText>
                                )}

                                {/* Remove button (works for both modes) */}
                                <Svg
                                  src={boardingRemove}
                                  onClick={async () => {
                                    if (state?.id && pointObj?.id != null) {
                                      await Location.removePoint({
                                        id: pointObj.id,
                                        state: 'location',
                                      })
                                    }
                                    const updatedList = [
                                      ...form.values.boardingPointsList,
                                    ]
                                    updatedList.splice(index, 1)
                                    form.setFieldValue(
                                      'boardingPointsList',
                                      updatedList,
                                    )
                                  }}
                                />
                              </StylePointCard>
                            )
                          },
                        )}
                      </AddedPointsWrapper>
                    )
                  }
                </Field>
              </Column>
            </LeftColumn>

            <RightColumn>
              <Fields.Upload
                name="image"
                label="Featured Image"
                variant="banner"
              />
            </RightColumn>
          </StyledFormRow>

          <br />

          <Button
            type="submit"
            title={state?.id ? 'Update Location' : 'Save Location'}
            justify="end"
            width={20}
            height={40}
            styleButton={`border-radius:18px;width:${isMobile && '100%'}`}
          />
        </Form>
      )}
    </Formik>
  )
}

export default LocationSetupForm
